import Hls from 'hls.js';

/**
 * Persistent per-source <video> cache for the showcase.
 *
 * Switching between highlights (or looping a clip) must NOT re-stream over the
 * network. We build ONE muted, looping <video> (+ hls.js instance) per source the
 * first time it is shown, fully buffer it (an unbounded back-buffer keeps the whole
 * short clip in memory), and keep it alive. The VideoStage just MOVES the cached
 * element into its container when that source becomes active; detached elements stay
 * buffered, so returning to a clip replays from memory with no new requests.
 *
 * HLS is locked to the TOP rendition (adaptive bitrate disabled) so the clip is
 * high-res from the first fragment instead of ramping low->high and dropping back
 * down mid-loop. LRU-capped so a long browsing session can't grow unbounded.
 */

type Entry = {
  video: HTMLVideoElement;
  hls?: Hls;
  failed: boolean;
  listeners: Set<() => void>;
};

const cache = new Map<string, Entry>();
const MAX_ENTRIES = 8;

const isHls = (src: string): boolean => /\.m3u8(\?|#|$)/i.test(src);

const create = (src: string, loop: boolean): Entry => {
  const video = document.createElement('video');
  video.muted = true;
  video.loop = loop;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.preload = 'auto';
  // The element lives outside React's tree (it is moved between containers), so its
  // layout styling is set imperatively here (no colours — the black frame is the box).
  video.style.width = '100%';
  video.style.height = '100%';
  video.style.objectFit = 'contain';

  const entry: Entry = { video, failed: false, listeners: new Set() };
  const fail = (): void => {
    if (entry.failed) return;
    entry.failed = true;
    entry.listeners.forEach((cb) => cb());
  };
  video.addEventListener('error', fail);

  if (!isHls(src)) {
    // Direct file (mp4/webm/…) — the browser's HTTP cache replays it with no refetch.
    video.src = src;
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // Safari — native HLS.
    video.src = src;
  } else if (Hls.isSupported()) {
    const hls = new Hls({
      autoStartLoad: false,
      // Don't cap quality to the (small) player box, and keep the whole played clip
      // buffered so a loop / return replays from memory instead of the network.
      capLevelToPlayerSize: false,
      backBufferLength: Infinity,
    });
    entry.hls = hls;
    hls.loadSource(src);
    hls.attachMedia(video);
    hls.once(Hls.Events.MANIFEST_PARSED, () => {
      // Pick the HIGHEST-resolution rendition by actual height/bitrate — don't assume the
      // manifest is ordered low->high (some CDNs, incl. Bunny, list it high->low, so the
      // old `levels.length - 1` locked to the LOWEST quality). Setting `currentLevel`
      // forces it AND disables ABR, so it stays high-res from the first fragment.
      const { levels } = hls;
      let best = 0;
      for (let i = 1; i < levels.length; i += 1) {
        const score = levels[i].height || levels[i].bitrate || 0;
        const bestScore = levels[best].height || levels[best].bitrate || 0;
        if (score > bestScore) best = i;
      }
      hls.startLevel = best;
      hls.currentLevel = best;
      hls.startLoad();
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) fail();
    });
  } else {
    entry.failed = true;
  }

  return entry;
};

const evict = (): void => {
  while (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    const entry = cache.get(oldestKey);
    entry?.hls?.destroy();
    entry?.video.remove();
    cache.delete(oldestKey);
  }
};

export type Clip = {
  video: HTMLVideoElement;
  failed: boolean;
  /** Subscribe to a load failure for this clip; returns an unsubscribe fn. */
  onFail: (cb: () => void) => () => void;
};

/** Get (or lazily create + start buffering) the cached clip for `src`. */
export const getClip = (src: string, loop: boolean): Clip => {
  let entry = cache.get(src);
  if (entry) {
    // LRU touch + keep the loop flag in sync.
    cache.delete(src);
    cache.set(src, entry);
    entry.video.loop = loop;
  } else {
    entry = create(src, loop);
    cache.set(src, entry);
    evict();
  }
  const current = entry;
  return {
    video: current.video,
    failed: current.failed,
    onFail: (cb) => {
      current.listeners.add(cb);
      return () => current.listeners.delete(cb);
    },
  };
};
